/**
 * Maps tracks to user sessions and stores optimal delays.
 *
 * - Maps trackId -> { userSessionId, type }
 * - Stores optimal delays per video track
 * - Provides method to get max optimal delay for a session by any track ID
 */
export class OptimalDelayMapper {
    // trackId -> TrackInfo
    tracks = new Map();
    // trackId -> optimalDelay (only for video tracks)
    trackOptimalDelays = new Map();
    /**
     * Register a track with its userSessionId and type.
     */
    registerTrackIfNotExists(trackId, userSessionId, type) {
        if (this.tracks.has(trackId)) {
            return;
        }
        this.tracks.set(trackId, { userSessionId, type });
    }
    /**
     * Update the optimal delay for a video track.
     */
    updateOptimalDelay(trackId, optimalDelay) {
        if (optimalDelay !== undefined) {
            this.trackOptimalDelays.set(trackId, optimalDelay);
        }
    }
    /**
     * Get the maximum optimal delay for a session.
     * Looks up the userSessionId from the given trackId,
     * then returns the max optimal delay among all VIDEO tracks in that session.
     */
    getOptimalDelayForSession(trackId) {
        const trackInfo = this.tracks.get(trackId);
        if (!trackInfo) {
            return undefined;
        }
        let maxOptimalDelay;
        // Find max optimal delay among video tracks in the same session
        for (const [tid, info] of this.tracks.entries()) {
            if (info.userSessionId === trackInfo.userSessionId &&
                info.type === 'video') {
                const optimalDelay = this.trackOptimalDelays.get(tid);
                if (optimalDelay !== undefined) {
                    if (maxOptimalDelay === undefined ||
                        optimalDelay > maxOptimalDelay) {
                        maxOptimalDelay = optimalDelay;
                    }
                }
            }
        }
        return maxOptimalDelay;
    }
    /**
     * Remove a track.
     */
    removeTrack(trackId) {
        this.tracks.delete(trackId);
        this.trackOptimalDelays.delete(trackId);
    }
    /**
     * Remove all tracks for a user session.
     */
    removeSession(userSessionId) {
        for (const [trackId, info] of this.tracks.entries()) {
            if (info.userSessionId === userSessionId) {
                this.tracks.delete(trackId);
                this.trackOptimalDelays.delete(trackId);
            }
        }
    }
    /**
     * Clear all mappings.
     */
    clear() {
        this.tracks.clear();
        this.trackOptimalDelays.clear();
    }
    /**
     * Get the userSessionId for a track.
     */
    getUserSessionIdForTrack(trackId) {
        return this.tracks.get(trackId)?.userSessionId;
    }
    /**
     * Get the type of a track.
     */
    getTrackType(trackId) {
        return this.tracks.get(trackId)?.type;
    }
    /**
     * Get all video track IDs in the same session as the given track.
     */
    getVideoTrackIdsInSameSession(trackId) {
        const trackInfo = this.tracks.get(trackId);
        if (!trackInfo) {
            return [];
        }
        const videoTrackIds = [];
        for (const [tid, info] of this.tracks.entries()) {
            if (info.userSessionId === trackInfo.userSessionId &&
                info.type === 'video') {
                videoTrackIds.push(tid);
            }
        }
        return videoTrackIds;
    }
}
